import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	type ConcreteRequest,
	type RecordSourceProxy,
	type RecordProxy,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderArgument,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';

export type WriteQueryInteropArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<DataProxy.WriteQueryOptions<TData, TVariables>, 'data' | 'variables'> & {
	query: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function writeQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__)
		? 'relay'
		: 'apollo';

	if (source === 'relay') {
		writeQueryInteropRelay<TData, TVariables>(params);
	} else {
		writeQueryInteropApollo<TData, TVariables>(params);
	}

	syncQueryCacheBridge({
		source,
		query: params.query,
		variables: params.variables,
		data: params.data,
	});
}

function writeQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: WriteQueryInteropArgs<TData, TVariables>,
): void {
	// Guard window for SSR; this only runs client-side.
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	const { query, data, variables } = params;

	if (!relayEnv || !data) {
		return;
	}

	relayEnv.commitUpdate((storeProxy: RecordSourceProxy) => {
		const root = storeProxy.getRoot();
		if (!root) {
			return;
		}

		const queryNode = query as ConcreteRequest;
		const queryFields = queryNode?.operation?.selections;

		if (queryFields) {
			writeQueryFields(
				storeProxy,
				root,
				queryFields as readonly ReaderSelection[],
				data,
				variables || {},
			);
		}
	});
}

// Bounded recursive walk of the query selection tree (mirrors readFields in readQuery.ts);
// the rule's recursion/iteration heuristic is a false positive here.
/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
function writeQueryFields(
	storeProxy: RecordSourceProxy,
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: any,
	variables: Record<string, any>,
): void {
	if (!record || !data) {
		return;
	}

	for (const field of fields) {
		if (field.kind === 'Condition') {
			// Honour @include/@skip so conditional fields (e.g. userPreferences) get written.
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;
			if (shouldInclude) {
				writeQueryFields(storeProxy, record, field.selections, data, variables);
			}
		} else if (field.kind === 'InlineFragment') {
			writeQueryFields(storeProxy, record, field.selections, data, variables);
		} else if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const fieldData = data[linkedField.name];
			if (fieldData == null || !linkedField.selections) {
				continue;
			}

			const fieldKey = getFieldKey(linkedField, variables);

			if (linkedField.plural) {
				if (!Array.isArray(fieldData)) {
					continue;
				}
				// Read plural fields with getLinkedRecords; the singular getter throws on
				// plural data. Existing records are updated in place, index-aligned with
				// fieldData (creating new records is out of scope — see singular branch).
				const existingRecords = record.getLinkedRecords(fieldKey);
				if (!existingRecords) {
					continue;
				}
				for (let index = 0; index < fieldData.length; index++) {
					const item = fieldData[index];
					const existingRecord = existingRecords[index];
					if (existingRecord && item && typeof item === 'object') {
						writeQueryFields(storeProxy, existingRecord, linkedField.selections, item, variables);
					}
				}
			} else if (typeof fieldData === 'object') {
				const linkedRecord = record.getLinkedRecord(fieldKey);

				// Only update existing records; creating new ones is out of scope.
				if (linkedRecord) {
					writeQueryFields(storeProxy, linkedRecord, linkedField.selections, fieldData, variables);
				}
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			if (data[scalarField.name] !== undefined) {
				record.setValue(data[scalarField.name], scalarField.name);
			}
		}
	}
}
/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */

function resolveArgValue(arg: ReaderArgument, variables: Record<string, any>): unknown {
	if (arg.kind === 'Literal') {
		return arg.value;
	}
	if (arg.kind === 'Variable') {
		return variables[arg.variableName];
	}
	// Fallback for other argument types
	return (arg as any).value;
}

function getFieldKey(field: ReaderLinkedField, variables: Record<string, any>): string {
	if (!field.args || field.args.length === 0) {
		return field.name;
	}

	// Mirror Relay's `formatStorageKey`: sort args alphabetically and omit null/undefined
	// values. Must stay in sync with `getFieldKey` in readQuery.ts so writes target the
	// same record the read found.
	const sortedArgs = field.args.slice().sort((a, b) => a.name.localeCompare(b.name));
	const formattedArgs: string[] = [];
	for (const arg of sortedArgs) {
		const value = resolveArgValue(arg, variables);
		if (value != null) {
			formattedArgs.push(`${arg.name}:${JSON.stringify(value)}`);
		}
	}

	return formattedArgs.length === 0 ? field.name : `${field.name}(${formattedArgs.join(',')})`;
}

function writeQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	return window['GLOBAL_APOLLO_CLIENT']!.writeQuery({
		...params,
		query: toDocumentNode(params.query),
	});
}
